import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/core';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { isEmpty } from 'lodash';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import Layout from '../../../components/Layout';
import {
  AppNavigationProp,
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { Role, TopicHelpdeskIssueItem } from '../../../client/interfaces';
import {
  Button,
  FormInput,
  FormErrorNotification,
  Modal,
  EditorInput,
  HelpdeskIssueSimpleTable,
} from '../../../components';
import { useAdmin } from '../../../contexts/Admin/AdminContext';
import { useAdminHelpdesk } from '../../../contexts/Admin/AdminHelpdeskContext';
import { useMessage } from '../../../contexts/Messages/MessageContext';
import { isWeb } from '../../../utils/responsive';
import client from '../../../client/client';
import { cln } from '../../../utils/classnames';

interface TopicSchema {
  nameHu: string;
  nameEn: string;
  backupDescriptionHu: string;
  backupDescriptionEn: string;
  isActive: boolean;
}

const emptyFormValues: TopicSchema = {
  nameHu: '',
  nameEn: '',
  backupDescriptionHu: '',
  backupDescriptionEn: '',
  isActive: true,
};

const AdminHelpdeskTopicEditScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminHelpdeskTopicEditScreen>
> = ({ navigation: nav, route }) => {
  const navigation = nav as AppNavigationProp;
  const { t } = useAppTranslation();
  const { me, hasRole } = useAuth();
  const { isBackModalOpen, setIsBackModalOpen } = useAdmin();
  const { refreshTopicsAndOrganizations } = useAdminHelpdesk();
  const isFocused = useIsFocused();

  const [initialFormValues, setInitialFormValues] = useState<TopicSchema>(emptyFormValues);
  const [helpdeskIssues, setHelpdeskIssues] = useState<TopicHelpdeskIssueItem[]>([]);
  const { setMessage } = useMessage();

  const isNewTopic = !route?.params?.topicId;
  const requiredTranslation = t('admin:common:required_field');
  const tooLongTranslation = (max) => t('admin:common:max_characters', { max });
  const duplicateTranslation = t('admin:helpdesk_topic_edit:name_already_exists');

  const topicSchema = Yup.object().shape({
    nameHu: Yup.string().required(requiredTranslation).max(60, tooLongTranslation(60)),
    nameEn: Yup.string().required(requiredTranslation).max(60, tooLongTranslation(60)),
    backupDescriptionHu: Yup.string()
      .required(requiredTranslation)
      .max(500, tooLongTranslation(100)),
    backupDescriptionEn: Yup.string()
      .required(requiredTranslation)
      .max(500, tooLongTranslation(100)),
    isActive: Yup.boolean(),
  });

  const onSubmit = async (
    values: TopicSchema,
    { setSubmitting, resetForm, setFieldError, setFieldTouched },
  ) => {
    try {
      if (isNewTopic) {
        await client.createHelpdeskTopic(values);
      } else {
        await client.updateHelpdeskTopic(route.params.topicId, values);
      }

      setSubmitting(false);
      resetForm({ values: emptyFormValues });
      await refreshTopicsAndOrganizations();
      if (route?.params?.returnScreen === ScreenName.AdminHelpdeskIssueEditScreen) {
        navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen, {
          postId: route.params.returnPostId,
        });
      } else {
        navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen);
      }
    } catch (e) {
      if (e.response?.data?.detail?.[0]?.msg === 'Names are not valid') {
        const loc = (e.response.data.detail[0].loc[1] as string).toLowerCase();
        if (loc.includes(values.nameHu.toLowerCase())) {
          setFieldError('nameHu', duplicateTranslation);
          setFieldTouched('nameHu', true, false);
        }
        if (loc.includes(values.nameEn.toLowerCase())) {
          setFieldError('nameEn', duplicateTranslation);
          setFieldTouched('nameEn', true, false);
        }
      } else {
        setMessage({ message: e.message, type: 'error' });
      }
    }
  };

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.publisher)) {
        navigation.navigate(ScreenName.HomeScreen);
      }
    }, [me?.roleList]),
  );

  useFocusEffect(
    useCallback(() => {
      if (isWeb) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }

      const fetchTopic = async () => {
        try {
          const topicRes = await client.getAdminHelpdeskTopic(route.params.topicId);
          setInitialFormValues(topicRes);
          setHelpdeskIssues(topicRes.helpdeskIssueList ?? []);
        } catch (e) {
          setMessage({ message: e.message, type: 'error' });
          navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen);
        }
      };

      if (route?.params?.topicId) {
        fetchTopic();
      } else {
        setInitialFormValues(emptyFormValues);
      }
    }, [route?.params?.topicId]),
  );

  return (
    <Layout
      title={
        isNewTopic
          ? t('admin:helpdesk_topic_edit:create_title')
          : t('admin:helpdesk_topic_edit:edit_title')
      }
    >
      <Formik
        initialValues={initialFormValues}
        validationSchema={topicSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
          isSubmitting,
          resetForm,
          validateForm,
          dirty,
        }) => (
          <View className="flex-1">
            <FormErrorNotification />
            <View
              className={cln(
                'flex-1 flex-row gap-x-20 z-30',
                isNewTopic || helpdeskIssues.length === 0 ? 'mb-10' : 'mb-0',
              )}
            >
              {/* Hungarian column */}
              <View className="flex-1">
                <FormInput
                  name="nameHu"
                  title={t('admin:helpdesk_topic_edit:title')}
                  placeholder={t('admin:common:max_characters', { max: 60 })}
                  required
                />
                {isWeb && (
                  <EditorInput
                    name="backupDescriptionHu"
                    title={t('admin:helpdesk_topic_edit:description')}
                    placeholder={t('admin:common:max_characters', { max: 100 })}
                    required
                    helperText={t('admin:helpdesk_topic_edit:description_helper')}
                  />
                )}
              </View>

              {/* English column */}
              <View className="flex-1">
                <FormInput
                  name="nameEn"
                  title={t('admin:helpdesk_topic_edit:title_en')}
                  placeholder={t('admin:common:max_characters', { max: 60 })}
                  required
                />
                {isWeb && (
                  <EditorInput
                    name="backupDescriptionEn"
                    title={t('admin:helpdesk_topic_edit:description_en')}
                    placeholder={t('admin:common:max_characters', { max: 100 })}
                    required
                    helperText={t('admin:helpdesk_topic_edit:description_helper')}
                  />
                )}
              </View>
            </View>
            {!isNewTopic && (
              <HelpdeskIssueSimpleTable
                rows={helpdeskIssues}
                title={t('admin:helpdesk_topic_edit:issues_table_title')}
                emptyText={t('admin:helpdesk_topic_edit:issues_table_empty')}
              />
            )}
            <View className="self-end flex-row gap-x-6">
              <Button
                text={t('admin:common:exit_from_edit')}
                size="medium"
                variant="link"
                onPress={() => {
                  setIsBackModalOpen(true);
                }}
                disabled={isSubmitting}
              />
              <Button
                text={values.isActive ? t('admin:common:save_changes') : t('admin:common:publish')}
                size="medium"
                variant="fill"
                onPress={async () => {
                  const validationErrors = await validateForm(values);
                  isEmpty(validationErrors) && (await setFieldValue('isActive', true));
                  handleSubmit();
                }}
                disabled={isSubmitting || (!dirty && (isNewTopic || values.isActive))}
              />
            </View>
            <Modal
              showModal={isBackModalOpen && isFocused}
              title={t('admin:common:exit_warning')}
              callback={() => {
                setIsBackModalOpen(false);
                resetForm({ values: emptyFormValues });
                if (route?.params?.returnScreen === ScreenName.AdminHelpdeskIssueEditScreen) {
                  navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen, {
                    postId: route.params.returnPostId,
                  });
                } else {
                  navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen);
                }
              }}
              closeModal={() => {
                setIsBackModalOpen(false);
              }}
              yesText={t('admin:common:exit')}
              cancelText={t('admin:common:stay')}
              instantCallback={!dirty}
            />
          </View>
        )}
      </Formik>
    </Layout>
  );
};

export default AdminHelpdeskTopicEditScreen;
